import {
  CartCouponTypeEnum,
  getCartCostPreviewDiscountCurrencyInfo,
  getCouponPercentLabel,
} from '@src/domains/coupons';
import { calculateItemTotalPriceSummary } from '@src/domains/cart/core/calculateItemTotalPriceSummary';
import type { Cart } from '../types/cart';

export const getCartProductCount = (cart: Cart | null | undefined): number => cart?.items?.length ?? 0;

export const getCartItemsTotalCents = (cart: Cart | null | undefined): number =>
  (cart?.items ?? []).reduce((sum, item) => sum + calculateItemTotalPriceSummary(item).total, 0);

const getCartCostPreviewDiscountCents = (cart: Cart | null | undefined): number => {
  if (!cart?.costPreview) {
    return 0;
  }

  return Math.max(getCartCostPreviewDiscountCurrencyInfo(cart).amountCents, 0);
};

export const getCartSubtotalCents = (cart: Cart | null | undefined): number => {
  if (!cart?.costPreview) {
    return 0;
  }

  const fullPartialCents = cart.costPreview.fullPartialAmountCurrencyInfo?.amountCents ?? 0;

  if (!isCartCouponApplied(cart)) {
    return fullPartialCents;
  }

  const costPreviewDiscountCents = getCartCostPreviewDiscountCents(cart);

  if (costPreviewDiscountCents > 0) {
    return fullPartialCents;
  }

  const totalCents = getCartTotalAmountCents(cart);
  const couponDiscountFromApi = cart.coupon?.discountAmountCurrencyInfo?.amountCents ?? 0;

  if (couponDiscountFromApi > 0) {
    return totalCents + couponDiscountFromApi;
  }

  const itemsTotalCents = getCartItemsTotalCents(cart);
  const partialAmountCents = cart.costPreview.partialAmountCurrencyInfo?.amountCents ?? 0;

  return Math.max(itemsTotalCents, partialAmountCents, fullPartialCents);
};

export const getCartCouponPercentLabel = (cart: Cart | null | undefined): string | undefined =>
  getCouponPercentLabel(isCartCouponApplied(cart) ? cart?.coupon : undefined, CartCouponTypeEnum.Percentage);

export const getCartTotalAmountCents = (cart: Cart | null | undefined): number =>
  cart?.costPreview?.totalAmountCurrencyInfo?.amountCents ?? 0;

export const getCartDiscountCents = (cart: Cart | null | undefined): number => {
  if (!cart?.costPreview) {
    return 0;
  }

  return Math.max(getCartSubtotalCents(cart) - getCartTotalAmountCents(cart), 0);
};

export const isCartCouponApplied = (cart: Cart | null | undefined): boolean =>
  Boolean(cart?.coupon?.code && !cart.coupon.invalid);

export const getCartCouponCode = (cart: Cart | null | undefined): string | undefined =>
  isCartCouponApplied(cart) ? cart?.coupon?.code : undefined;

export const getCartCouponDiscountCents = (cart: Cart | null | undefined, fallbackDiscountCents = 0): number => {
  if (!isCartCouponApplied(cart)) {
    return 0;
  }

  const totalDiscountCents = getCartDiscountCents(cart);

  if (totalDiscountCents <= 0) {
    return 0;
  }

  const couponDiscountFromApi = cart?.coupon?.discountAmountCurrencyInfo?.amountCents;

  if (couponDiscountFromApi != null) {
    return Math.min(Math.max(couponDiscountFromApi, 0), totalDiscountCents);
  }

  return Math.min(Math.max(fallbackDiscountCents, 0), totalDiscountCents);
};
